/**
 * Client-side try-on job registry using useSyncExternalStore.
 * Stores results in IndexedDB for offline persistence.
 */

import { readApiKey } from "./apiKey";

const DB_NAME = "dressingroom-db";
const DB_VERSION = 1;
const RESULTS_STORE = "try-on-results";
const MAX_RESULTS = 24;

export type JobPhase = "measuring" | "grading" | "resolving" | "rendering" | "done" | "error";

export interface TryOnRequest {
  productId: string;
  productName: string;
  size: string;
  color: string;
  colorHex: string;
  fit: string;
  profile: Record<string, unknown>;
  referencePhotos?: { front?: string; side?: string; back?: string };
}

export interface TryOnResult {
  imageUrl: string;
  fitReport: unknown;
}

export interface TryOnJob {
  key: string;
  request: TryOnRequest;
  status: "idle" | "running" | "done" | "error";
  phase: JobPhase;
  progress: number;
  result?: TryOnResult;
  error?: string;
  createdAt: number;
}

type Listener = () => void;

const jobs = new Map<string, TryOnJob>();
const listeners = new Set<Listener>();
let phaseTimer: ReturnType<typeof setInterval> | null = null;

function emit() {
  listeners.forEach((fn) => fn());
}

function jobKey(req: TryOnRequest): string {
  return [req.productId, req.size, req.color, req.fit].join("|");
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(RESULTS_STORE)) {
        db.createObjectStore(RESULTS_STORE);
      }
      if (!db.objectStoreNames.contains("profile-photos")) {
        db.createObjectStore("profile-photos");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function cacheResult(key: string, job: TryOnJob): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(RESULTS_STORE, "readwrite");
    tx.objectStore(RESULTS_STORE).put(job, key);
    // Prune old entries
    const allKeys = await new Promise<IDBValidKey[]>((resolve, reject) => {
      const r = tx.objectStore(RESULTS_STORE).getAllKeys();
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    if (allKeys.length > MAX_RESULTS) {
      // Delete oldest entries beyond MAX_RESULTS
      const tx2 = db.transaction(RESULTS_STORE, "readwrite");
      const store = tx2.objectStore(RESULTS_STORE);
      for (let i = 0; i < allKeys.length - MAX_RESULTS; i++) {
        store.delete(allKeys[i]);
      }
    }
  } catch {
    // non-fatal
  }
}

export async function hydrate(req: TryOnRequest): Promise<void> {
  const key = jobKey(req);
  if (jobs.has(key)) return;
  try {
    const db = await openDB();
    const cached = await new Promise<TryOnJob | undefined>((resolve, reject) => {
      const tx = db.transaction(RESULTS_STORE, "readonly");
      const r = tx.objectStore(RESULTS_STORE).get(key);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    if (cached && cached.status === "done" && cached.result) {
      jobs.set(key, cached);
      emit();
    }
  } catch {
    // non-fatal
  }
}

function startPhaseSimulation(key: string) {
  if (phaseTimer) clearInterval(phaseTimer);
  const phases: { phase: JobPhase; target: number }[] = [
    { phase: "measuring", target: 25 },
    { phase: "grading", target: 50 },
    { phase: "resolving", target: 75 },
    { phase: "rendering", target: 95 },
  ];
  let phaseIdx = 0;
  let current = 0;
  phaseTimer = setInterval(() => {
    const job = jobs.get(key);
    if (!job || job.status !== "running") {
      if (phaseTimer) clearInterval(phaseTimer);
      return;
    }
    const target = phases[phaseIdx]?.target ?? 95;
    current = Math.min(current + Math.random() * 8 + 3, target);
    job.progress = Math.round(current);
    job.phase = phases[phaseIdx]?.phase ?? "rendering";
    if (current >= target && phaseIdx < phases.length - 1) {
      phaseIdx++;
    }
    emit();
  }, 600);
}

export async function startJob(req: TryOnRequest): Promise<void> {
  const key = jobKey(req);

  // Check for cached completed job
  const existing = jobs.get(key);
  if (existing && existing.status === "done") return;

  const job: TryOnJob = {
    key,
    request: req,
    status: "running",
    phase: "measuring",
    progress: 0,
    createdAt: Date.now(),
  };
  jobs.set(key, job);
  emit();
  startPhaseSimulation(key);

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const apiKey = readApiKey();
    if (apiKey) {
      headers["x-openai-key"] = apiKey;
    }

    const res = await fetch("/api/try-on", {
      method: "POST",
      headers,
      body: JSON.stringify({
        profile: req.profile,
        product: {
          id: req.productId,
          size: req.size,
          color: req.color,
          colorHex: req.colorHex,
          fit: req.fit,
        },
        referencePhotos: req.referencePhotos,
      }),
    });
    const data = await res.json();

    if (phaseTimer) clearInterval(phaseTimer);

    if (!data.success) {
      job.status = "error";
      job.phase = "error";
      job.error = data.error || "Generation failed";
      emit();
      return;
    }

    job.status = "done";
    job.phase = "done";
    job.progress = 100;
    job.result = {
      imageUrl: data.imageUrl,
      fitReport: data.fitReport,
    };
    emit();
    await cacheResult(key, job);
  } catch (err) {
    if (phaseTimer) clearInterval(phaseTimer);
    job.status = "error";
    job.phase = "error";
    job.error = err instanceof Error ? err.message : "Network error";
    emit();
  }
}

export function cancel(jobKeyStr: string): void {
  const job = jobs.get(jobKeyStr);
  if (job && job.status === "running") {
    job.status = "error";
    job.error = "Cancelled";
    if (phaseTimer) clearInterval(phaseTimer);
    emit();
  }
}

export function clearAll(): void {
  jobs.clear();
  if (phaseTimer) clearInterval(phaseTimer);
  emit();
}

export function getJob(req: TryOnRequest): TryOnJob | undefined {
  return jobs.get(jobKey(req));
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): typeof jobs {
  return jobs;
}

const EMPTY_MAP = new Map<string, TryOnJob>();

export function getServerSnapshot(): Map<string, TryOnJob> {
  return EMPTY_MAP;
}

export { jobKey };
